import React from 'react';
import styled from 'styled-components';
import useImageOrientation from '../../../../../hooks/useImageOrientation';
import { isJpgImage } from '../../../../../utils/imageHelpers';

const SelectedPosesAndBackgroundsContainer = styled.div`
  padding-bottom: 32px;
`;

const StudentPreviewContainer = styled.div`
  margin-bottom: 16px;
  float: none;
`;

const SelectedConfigWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  z-index: 2;
  &:hover {
    cursor: pointer;
  }
`;

const Box = styled.div`
  display: flex;
  align-items: center;
  height: ${(props) => {
    return props.is_horizontal ? '120px' : '150px';
  }};
  width: ${(props) => {
    return props.is_horizontal ? '150px' : '120px';
  }};
  background-color: #e0dedf;
  position: relative;
  background-image: ${(props) => props.background};
  background-size: cover;
  background-position: center;
  border: 1px solid darkgrey;
  margin: 0 12px;
  margin-top: ${(props) => (props.isShifted ? '24px' : '0')};
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  img {
    object-fit: cover;
    width: 100%;
    height: 100%;
  }
`;

const BoxText = styled.div`
  position: absolute;
  bottom: -24px;
  left: 50%;
  transform: translateX(-50%);
  color: #555555;
  font-size: 14px;
`;

const SelectedPosesAndBackgrounds = ({
  backgroundSelections,
  poseSelections,
  isGeskus,
  isGreenScreen,
  isPrepay,
  picsGalleryId,
  posesCount,
  productBackgroundOptionsCount,
  studentPreviewImageUrl,
  onEditSelection = () => {},
  togglePosesBackgroundsSelector, // TODO: remove this(?)
}) => {
  const imageUrls = [
    ...poseSelections.map((pose) =>
      pose.selected_crop ? pose.selected_crop.url : pose.url,
    ),
    ...(!isPrepay && studentPreviewImageUrl ? [studentPreviewImageUrl] : []),
  ].filter(Boolean);

  const { imageOrientations } = useImageOrientation(imageUrls);

  let productRequiresConfiguration = true;
  productRequiresConfiguration =
    productBackgroundOptionsCount && productBackgroundOptionsCount > 0;

  const renderBackgroundImage = (index, backgroundOptionsCount) => {
    if (backgroundSelections.length > 0 && backgroundSelections[index]) {
      return (
        <>
          {!isPrepay && studentPreviewImageUrl && (
            <StudentPreviewContainer className="bnl-product-config__selected-pose">
              <img
                className="bnl-product-config__selected-pose__image portrait-image"
                src={studentPreviewImageUrl}
              />
            </StudentPreviewContainer>
          )}
          <div className="bnl-product-config__selected-background bnl-product-config__selected-background--pre-shoot">
            <img
              className="bnl-product-config__selected-background__image"
              src={backgroundSelections[index]?.image_thumbnail}
            />
          </div>
        </>
      );
    }

    return (
      <>
        <div style={{ position: 'relative' }}>
          {!isPrepay && studentPreviewImageUrl && (
            <StudentPreviewContainer className="bnl-product-config__selected-pose">
              <img
                className="bnl-product-config__selected-pose__image portrait-image"
                src={studentPreviewImageUrl}
              />
            </StudentPreviewContainer>
          )}
        </div>
        <div style={{ position: 'relative' }}>
          <div className="bnl-product-config__selected-background__image"></div>
          <div className="bnl-product-config__selected-background__desc">
            {backgroundOptionsCount > 1
              ? `Background ${index + 1}`
              : `Background`}
          </div>
        </div>
      </>
    );
  };

  const renderBackgroundPosesContainer = (
    toggleMobilePosesBackgroundsConfig = false,
    productBackgroundOptionsCount = 0,
    posesCount = 0,
  ) => {
    if (!productRequiresConfiguration) {
      return null;
    }

    // In some scenarios, the product will have one pose but two backgrounds...
    // we need to utilize the higher value of the two
    const greaterOfTheTwo =
      productBackgroundOptionsCount > posesCount
        ? productBackgroundOptionsCount
        : posesCount;

    const containers = [];

    for (let i = 0; i < greaterOfTheTwo; i++) {
      containers.push(
        <SelectedConfigWrapper
          key={`selected-config-${i}`}
          className=""
          onClick={() => {
            toggleMobilePosesBackgroundsConfig
              ? togglePosesBackgroundsSelector()
              : false;
          }}
        >
          {renderPose(i)}
          {renderBackground(i)}
        </SelectedConfigWrapper>,
      );
    }

    return containers;
  };

  const renderPose = (index) => {
    const background = backgroundSelections[index]
      ? `url(${backgroundSelections[index]?.image_thumbnail})`
      : '';
    const handleEditPose = (event) => {
      event.stopPropagation();
      onEditSelection(index, 'pose');
    };

    if (poseSelections.length > 0 && poseSelections[index]) {
      const pose = poseSelections[index];

      const imageUrl = pose.selected_crop ? pose.selected_crop.url : pose.url;
      const is_horizontal = imageOrientations[imageUrl] || false;

      // Don't layer JPG images over backgrounds - they don't have alpha transparency
      // For JPGs: show pose only (no background behind it)
      // For PNGs: layer pose over background
      const isJpg = isJpgImage(imageUrl);
      const boxBackground = isJpg ? '' : background;

      if (!!pose.selected_crop) {
        return (
          <Box
            background={boxBackground}
            is_horizontal={is_horizontal}
            onClick={handleEditPose}
          >
            <img
              onContextMenu={(event) => event.preventDefault()}
              src={pose.selected_crop.url}
            />
            {productBackgroundOptionsCount - 1 === index && (
              <BoxText>pose</BoxText>
            )}
          </Box>
        );
      }
      return (
        <Box
          isShifted={index > 0}
          background={boxBackground}
          is_horizontal={is_horizontal}
          onClick={handleEditPose}
        >
          <img
            onContextMenu={(event) => event.preventDefault()}
            src={poseSelections[index].url}
          />
          {productBackgroundOptionsCount - 1 === index && (
            <BoxText>pose</BoxText>
          )}
        </Box>
      );
    }

    if (index >= posesCount) {
      return null;
    }

    // Note: studentPreviewImageUrl should only be available for Geskus New England; we
    // don't want to display it for a PicsGallery-driven order
    if (!isPrepay && studentPreviewImageUrl && !picsGalleryId && isGeskus) {
      const preview_is_horizontal =
        imageOrientations[studentPreviewImageUrl] || false;

      // Don't layer JPG images over backgrounds - they don't have alpha transparency
      // For JPGs: show preview only (no background behind it)
      // For PNGs: layer preview over background
      const isJpg = isJpgImage(studentPreviewImageUrl);
      const boxBackground = isJpg ? '' : background;

      return (
        <Box
          isShifted={index > 0}
          background={boxBackground}
          is_horizontal={preview_is_horizontal}
          onClick={handleEditPose}
        >
          <img
            onContextMenu={(event) => event.preventDefault()}
            src={studentPreviewImageUrl}
          />
          {productBackgroundOptionsCount - 1 === index && (
            <BoxText>pose</BoxText>
          )}
        </Box>
      );
    }

    if (!isPrepay) {
      return (
        <Box
          isShifted={index > 0}
          is_horizontal={false}
          onClick={handleEditPose}
        >
          {productBackgroundOptionsCount - 1 === index && (
            <BoxText>pose</BoxText>
          )}
        </Box>
      );
    }

    return null;
  };

  const renderBackground = (index) => {
    const handleEditBackground = (event) => {
      event.stopPropagation();
      onEditSelection(index, 'background');
    };

    if (backgroundSelections.length > 0 && backgroundSelections[index]) {
      return (
        <Box
          isShifted={index > 0}
          background={`url(${backgroundSelections[index]?.image_thumbnail})`}
          onClick={handleEditBackground}
        >
          {productBackgroundOptionsCount - 1 === index && (
            <BoxText>background</BoxText>
          )}
        </Box>
      );
    }

    if (index >= productBackgroundOptionsCount) {
      return null;
    }

    if (productBackgroundOptionsCount > 0 && isGreenScreen) {
      return (
        <Box isShifted={index > 0} onClick={handleEditBackground}>
          {productBackgroundOptionsCount - 1 === index && (
            <BoxText>background</BoxText>
          )}
        </Box>
      );
    }

    return null;
  };

  const renderTapToConfigure = () => {
    if (!productRequiresConfiguration) {
      return null;
    }

    return (
      <>
        <div className="bnl-product-config__mobile-instruction">
          Tap to configure
        </div>
        {renderBackgroundPosesContainer(
          true,
          productBackgroundOptionsCount,
          posesCount,
        )}
      </>
    );
  };

  return (
    <SelectedPosesAndBackgroundsContainer>
      <div className="poses-backgrounds-container__mobile">
        {renderTapToConfigure()}
      </div>
      <div className="poses-backgrounds-container__desktop">
        {renderBackgroundPosesContainer(
          true,
          productBackgroundOptionsCount,
          posesCount,
        )}
      </div>
    </SelectedPosesAndBackgroundsContainer>
  );
};

export default SelectedPosesAndBackgrounds;
